class WelcomeBannerManager {
    constructor() {
        this.banners = new Map();
        this.observers = new Map();
    }

    // Initialize a banner with fade-in effect
    initializeBanner(bannerId, fadeDuration = 1000) {
        const banner = document.querySelector(`[data-banner-id="${bannerId}"]`);

        if (!banner) {
            console.warn(`Banner with ID ${bannerId} not found`);
            return;
        }

        // Store banner reference
        this.banners.set(bannerId, banner);

        // Set CSS custom property for fade duration
        banner.style.setProperty('--fade-duration', `${fadeDuration}ms`);

        // Trigger fade-in animation
        this.fadeInBanner(banner, fadeDuration);

        // Set up intersection observer for performance optimization
        this.setupIntersectionObserver(bannerId, banner);

        console.log(`WelcomeBanner ${bannerId} initialized`);
    }

    // Handle fade-in animation
    fadeInBanner(banner, duration) {
        // Ensure the banner starts invisible
        banner.style.opacity = '0';

        // Force a reflow to ensure the opacity change is applied
        banner.offsetHeight;

        // Add fade-in class and set opacity
        banner.classList.add('fade-in');

        // Use requestAnimationFrame for smoother animation
        requestAnimationFrame(() => {
            banner.style.opacity = '1';
        });

        // Clean up after animation completes
        setTimeout(() => {
            banner.classList.remove('fade-in');
        }, duration);
    }

    // Set up intersection observer to optimize animations when banner is not visible
    setupIntersectionObserver(bannerId, banner) {
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const documents = entry.target.querySelectorAll('.document');

                    if (entry.isIntersecting) {
                        // Resume animations when banner is visible
                        documents.forEach(doc => {
                            doc.style.animationPlayState = 'running';
                        });
                    } else {
                        // Pause animations when banner is not visible (performance optimization)
                        documents.forEach(doc => {
                            doc.style.animationPlayState = 'paused';
                        });
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: '50px'
            });

            observer.observe(banner);
            this.observers.set(bannerId, observer);
        }
    }

    // Update banner properties dynamically
    updateBanner(bannerId, properties) {
        const banner = this.banners.get(bannerId);

        if (!banner) {
            console.warn(`Banner with ID ${bannerId} not found`);
            return;
        }

        // Update CSS custom properties
        Object.entries(properties).forEach(([key, value]) => {
            banner.style.setProperty(`--${key}`, value);
        });
    }

    // Trigger a re-animation of the documents
    reanimateDocuments(bannerId) {
        const banner = this.banners.get(bannerId);

        if (!banner) {
            console.warn(`Banner with ID ${bannerId} not found`);
            return;
        }

        const documents = banner.querySelectorAll('.document');

        documents.forEach(doc => {
            // Temporarily remove animation
            const originalAnimation = doc.style.animation;
            doc.style.animation = 'none';

            // Force reflow
            doc.offsetHeight;

            // Restore animation
            doc.style.animation = originalAnimation;
        });
    }

    // Clean up resources
    disposeBanner(bannerId) {
        // Clean up intersection observer
        const observer = this.observers.get(bannerId);
        if (observer) {
            observer.disconnect();
            this.observers.delete(bannerId);
        }

        // Remove banner reference
        this.banners.delete(bannerId);

        console.log(`WelcomeBanner ${bannerId} disposed`);
    }

    // Get banner statistics (for debugging)
    getBannerStats(bannerId) {
        const banner = this.banners.get(bannerId);

        if (!banner) {
            return null;
        }

        const rect = banner.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(banner);

        return {
            id: bannerId,
            visible: rect.width > 0 && rect.height > 0,
            dimensions: {
                width: rect.width,
                height: rect.height
            },
            position: {
                top: rect.top,
                left: rect.left
            },
            opacity: computedStyle.opacity,
            documentCount: banner.querySelectorAll('.document').length
        };
    }
}

// Create global instance
const bannerManager = new WelcomeBannerManager();

// Export functions for Blazor interop
export function initializeBanner(bannerId, fadeDuration) {
    bannerManager.initializeBanner(bannerId, fadeDuration);
}

export function updateBanner(bannerId, properties) {
    bannerManager.updateBanner(bannerId, properties);
}

export function reanimateDocuments(bannerId) {
    bannerManager.reanimateDocuments(bannerId);
}

export function disposeBanner(bannerId) {
    bannerManager.disposeBanner(bannerId);
}

export function getBannerStats(bannerId) {
    return bannerManager.getBannerStats(bannerId);
}

// Optional: Global access for debugging
if (typeof window !== 'undefined') {
    window.WelcomeBannerManager = bannerManager;
}